import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { useSelector } from "react-redux";
import apiClient from "../../utils/apiClient";
import Display from "../../core/common/display/display";
import useCashSession from "./useCashSession";

const INQ_CHUNK_SIZE = 120;

const chunkArray = (list = [], chunkSize = INQ_CHUNK_SIZE) => {
  const chunks = [];
  for (let i = 0; i < list.length; i += chunkSize) {
    chunks.push(list.slice(i, i + chunkSize));
  }
  return chunks;
};

const formatNumber = (value) => {
  const numberValue = Number(value) || 0;
  return new Intl.NumberFormat("fr-FR", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numberValue);
};

const CashSessionModal = ({ open, onClose }) => {
  const { t, i18n } = useTranslation();
  const user_infos = useSelector((state) => state.rootReducer.user_infos);
  const { cashSession, refreshCashSession, current_location } = useCashSession();
  const [initialCash, setInitialCash] = useState("");
  const [declaredCash, setDeclaredCash] = useState("");
  const [note, setNote] = useState("");
  const [loading, setLoading] = useState(false);
  const [display, setDisplay] = useState({ type: "", message: "", open: false });
  const [itemTopEmployees, setItemTopEmployees] = useState([]);

  const activeSession = cashSession?.session;
  const metrics = cashSession?.metrics;
  const currency = current_location?.hasCurrency?.currency || "XOF";
  const locale = i18n?.language || "fr-FR";

  const formatDateTime = (value) => {
    if (!value) return "-";
    try {
      return new Date(value).toLocaleString(locale);
    } catch (_) {
      return value;
    }
  };

  useEffect(() => {
    if (metrics?.theoreticalCash !== undefined && metrics?.theoreticalCash !== null) {
      const fallback = metrics.declaredCashAmount ?? metrics.theoreticalCash;
      setDeclaredCash(String(fallback ?? ""));
    }
  }, [metrics]);

  useEffect(() => {
    if (open) {
      refreshCashSession();
    }
  }, [open, refreshCashSession]);

  const difference = useMemo(() => {
    if (!metrics) return 0;
    const declared = Number(declaredCash) || 0;
    return declared - (metrics.theoreticalCash || 0);
  }, [declaredCash, metrics]);

  const handleOpenSession = async () => {
    if (!current_location?._id) return;
    setLoading(true);
    try {
      await apiClient.post(`/locations/${current_location._id}/cash-sessions/open`, {
        initialCashFund: Number(initialCash) || 0,
        notes: note || undefined,
        openedBy: user_infos?.id,
        _establishmentId: user_infos?.establishmentId,
      });
      setDisplay({ type: "success", message: t("cash session opened", "Session de caisse ouverte"), open: true });
      setInitialCash("");
      setNote("");
      await refreshCashSession();
    } catch (error) {
      setDisplay({
        type: "error",
        message: error?.response?.data?.error?.message || t("unable to open cash session", "Impossible d'ouvrir la session de caisse"),
        open: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCloseSession = async () => {
    if (!activeSession?._id) return;
    setLoading(true);
    try {
      await apiClient.post(`/cash-sessions/${activeSession._id}/close`, {
        declaredCashAmount: Number(declaredCash) || 0,
        notes: note || undefined,
        closedBy: user_infos?.id,
      });
      setDisplay({ type: "success", message: t("cash session closed", "Clôture de caisse effectuée"), open: true });
      await refreshCashSession();
    } catch (error) {
      setDisplay({
        type: "error",
        message: error?.response?.data?.error?.message || t("unable to close cash session", "Impossible de clôturer la caisse"),
        open: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const closeAndReset = () => {
    setNote("");
    setInitialCash("");
    setDeclaredCash("");
    onClose?.();
  };

  useEffect(() => {
    const fetchTopEmployeesFromItems = async () => {
      const session = cashSession?.session;
      const establishmentId = user_infos?.establishmentId;
      if (!open || !session?._id || !current_location?._id || !session?.openedAt) {
        setItemTopEmployees([]);
        return;
      }

      try {
        const filter = {
          where: {
            and: [
              { _locationId: current_location._id },
              { createdAt: { gte: session.openedAt } },
              { createdAt: { lte: new Date().toISOString() } },
            ],
          },
          order: ["createdAt DESC"],
          fields: {
            _id: true,
            item: true,
            totalAmount: true,
            createdAt: true,
          },
        };

        const [invoicesRes, employeesRes] = await Promise.all([
          apiClient.get("/invoices", {
            params: { filter: JSON.stringify(filter) },
          }),
          establishmentId
            ? apiClient
              .get(`/establishments/${establishmentId}/employees`, {
                params: {
                  filter: JSON.stringify({
                    fields: {
                      _id: true,
                      firstname: true,
                      lastname: true,
                      serial: true,
                      phone: true,
                    },
                    limit: 500,
                  }),
                },
              })
              .catch(() => ({ data: [] }))
            : Promise.resolve({ data: [] }),
        ]);

        const invoices = Array.isArray(invoicesRes.data) ? invoicesRes.data : [];
        const employees = Array.isArray(employeesRes.data) ? employeesRes.data : [];
        const invoiceIds = invoices.map((invoice) => invoice?._id).filter(Boolean);
        const invoiceLinks = [];

        if (invoiceIds.length) {
          for (const invoiceIdChunk of chunkArray(invoiceIds)) {
            if (!invoiceIdChunk.length) continue;
            try {
              const linksRes = await apiClient.get("/employee-invoices", {
                params: {
                  filter: JSON.stringify({
                    where: { _invoiceId: { inq: invoiceIdChunk } },
                    fields: {
                      _invoiceId: true,
                      _employeeId: true,
                    },
                    limit: 1000,
                  }),
                },
              });
              const links = Array.isArray(linksRes?.data) ? linksRes.data : [];
              invoiceLinks.push(...links);
            } catch (_) {
              // keep going; item-level assignment can still provide most metrics
            }
          }
        }

        const linksByInvoice = new Map();
        invoiceLinks.forEach((link) => {
          if (!link?._invoiceId || !link?._employeeId) return;
          const list = linksByInvoice.get(link._invoiceId) || [];
          list.push(link);
          linksByInvoice.set(link._invoiceId, list);
        });

        const nameMap = new Map();
        const toNumber = (val) => {
          const num = Number(val);
          return Number.isFinite(num) ? num : 0;
        };
        employees.forEach((emp) => {
          if (!emp?._id) return;
          const fullName =
            [emp.firstname, emp.lastname].filter(Boolean).join(" ") ||
            emp.serial ||
            emp.phone ||
            t("unknown user", "Unknown user");
          nameMap.set(String(emp._id), fullName);
        });

        const totals = new Map();
        const addTotal = (id, amount) => {
          const value = toNumber(amount);
          if (!id || value <= 0) return;
          const key = String(id);
          totals.set(key, (totals.get(key) || 0) + value);
          if (!nameMap.has(key)) {
            // Capture a label from the invoice if provided
            nameMap.set(
              key,
              t("unknown user", "Unknown user"),
            );
          }
        };

        invoices.forEach((inv) => {
          const items = Array.isArray(inv?.item) ? inv.item : [];
          const itemEntries = items
            .map((item) => {
              const assigned = Array.isArray(item?.employees) ? item.employees : [];
              assigned.forEach((emp) => {
                const id = emp?.value || emp?._id || emp;
                if (!id) return;
                const label =
                  emp?.label ||
                  [emp?.firstname, emp?.lastname].filter(Boolean).join(" ") ||
                  emp?.serial ||
                  emp?.phone;
                if (label && !nameMap.has(String(id))) {
                  nameMap.set(String(id), label);
                }
              });
              const employeeIds = assigned
                .map((emp) => emp?.value || emp?._id || emp)
                .filter(Boolean)
                .map((id) => String(id));
              if (!employeeIds.length) return null;
              const price = toNumber(item?.price);
              const quantity = toNumber(item?.quantity || 1);
              return { employeeIds, gross: price * quantity };
            })
            .filter(Boolean);

          if (itemEntries.length) {
            const grossTotal = itemEntries.reduce((sum, entry) => sum + toNumber(entry.gross), 0);
            const netTotal = toNumber(inv?.totalAmount);
            const hasGross = grossTotal > 0;
            const scalingFactor = hasGross && netTotal > 0 ? netTotal / grossTotal : 0;
            const fallbackValuePerEntry = !hasGross && itemEntries.length ? netTotal / itemEntries.length : 0;

            itemEntries.forEach((entry) => {
              const rawValue = hasGross ? entry.gross * scalingFactor : fallbackValuePerEntry;
              const value = toNumber(rawValue);
              if (value <= 0 || !entry.employeeIds.length) return;
              const share = value / entry.employeeIds.length;
              entry.employeeIds.forEach((empId) => addTotal(empId, share));
            });
          } else {
            const fallbackLinks = linksByInvoice.get(inv?._id) || [];
            if (!fallbackLinks.length) return;
            const netTotal = toNumber(inv?.totalAmount);
            if (netTotal > 0) {
              const share = netTotal / fallbackLinks.length;
              fallbackLinks.forEach((link) => {
                const id = link?._employeeId;
                addTotal(id, share);
              });
            }
          }
        });

        const top = Array.from(totals.entries())
          .map(([employeeId, total]) => ({
            employeeId,
            total,
            name: nameMap.get(employeeId) || t("unknown user", "Unknown user"),
          }))
          .sort((a, b) => b.total - a.total)
          .slice(0, 5);

        setItemTopEmployees(top);
      } catch (error) {
        // eslint-disable-next-line no-console
        console.error("Failed to compute item-level top employees", error);
        setItemTopEmployees([]);
      }
    };

    fetchTopEmployeesFromItems();
  }, [cashSession?.session, current_location?._id, open, t, user_infos?.establishmentId]);

  if (!open) {
    return <Display handleClose={() => setDisplay({ ...display, open: false })} {...display} />;
  }

  const displayedTopEmployees = itemTopEmployees.length ? itemTopEmployees : metrics?.topEmployees || [];

  return (
    <>
      <div tabIndex={-1} className="modal fade show" style={{ display: "block" }} aria-modal="true" role="dialog">
        <div className="modal-dialog modal-lg modal-dialog-centered">
          <div className="modal-content">
            <div className="modal-header">
              <h5 className="modal-title">
                {activeSession ? t("end of day", "Clôture de caisse") : t("open cash session", "Ouvrir la caisse")}
              </h5>
              <button type="button" className="btn-close" aria-label={t("close", "Fermer")} onClick={closeAndReset} />
            </div>
            <div className="modal-body">
              {activeSession ? (
                <>
                  <div className="alert alert-info d-flex justify-content-between align-items-center">
                    <div>
                      <strong>{t("session opened at", "Session ouverte à")} </strong>
                      {formatDateTime(activeSession.openedAt)}
                    </div>
                    <span className="badge bg-success">{t("status open", "Ouverte")}</span>
                  </div>

                  <div className="row g-3 mb-3">
                    <div className="col-md-4">
                      <div className="card h-100 border-0 shadow-sm">
                        <div className="card-body">
                          <p className="text-muted mb-1">{t("total sales", "Total ventes")}</p>
                          <h4 className="mb-0">{formatNumber(metrics?.totalSales)} {currency}</h4>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="card h-100 border-0 shadow-sm">
                        <div className="card-body">
                          <p className="text-muted mb-1">{t("theoretical cash", "Montant cash théorique")}</p>
                          <h4 className="mb-0">{formatNumber(metrics?.theoreticalCash)} {currency}</h4>
                          <small className="text-muted">{t("initial float", "Fond de caisse")}: {formatNumber(metrics?.initialCashFund)} {currency}</small>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="card h-100 border-0 shadow-sm">
                        <div className="card-body">
                          <p className="text-muted mb-1">{t("expenses", "Dépenses")}</p>
                          <h4 className="mb-0">{formatNumber(metrics?.totalExpenses)} {currency}</h4>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="row g-3 mb-4">
                    <div className="col-md-6">
                      <div className="card h-100">
                        <div className="card-body">
                          <h6 className="mb-3">{t("payments breakdown", "Répartition par mode de paiement")}</h6>
                          <ul className="list-unstyled mb-0">
                            {metrics?.paymentBreakdown ? (
                              Object.entries(metrics.paymentBreakdown).map(([method, value]) => (
                                <li key={method} className="d-flex justify-content-between mb-1">
                                  <span className="text-capitalize">{method}</span>
                                  <span className="fw-semibold">{formatNumber(value)} {currency}</span>
                                </li>
                              ))
                            ) : (
                              <li className="text-muted">{t("no data", "Aucune donnée")}</li>
                            )}
                          </ul>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="card h-100">
                        <div className="card-body">
                          <h6 className="mb-3">{t("invoices summary", "Factures")}</h6>
                          <div className="d-flex flex-wrap gap-2">
                            <span className="badge bg-dark">{t("total", "Total")}: {metrics?.invoiceCount?.total || 0}</span>
                            <span className="badge bg-success">{t("paid", "Payées")}: {metrics?.invoiceCount?.paid || 0}</span>
                            <span className="badge bg-warning text-dark">{t("on hold", "En attente")}: {metrics?.invoiceCount?.onHold || 0}</span>
                            <span className="badge bg-danger">{t("unpaid", "Impayées")}: {metrics?.invoiceCount?.unpaid || 0}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="row g-3 mb-4">
                    <div className="col-md-6">
                      <div className="card h-100">
                        <div className="card-body">
                          <h6 className="mb-3">{t("top employees", "Top employés")}</h6>
                          <ul className="list-unstyled mb-0">
                            {displayedTopEmployees?.length ? (
                              displayedTopEmployees.map((emp) => (
                                <li key={emp.employeeId} className="d-flex justify-content-between mb-1">
                                  <span>{emp.name}</span>
                                  <span className="fw-semibold">{formatNumber(emp.total)} {currency}</span>
                                </li>
                              ))
                            ) : (
                              <li className="text-muted">{t("no data", "Aucune donnée")}</li>
                            )}
                          </ul>
                        </div>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="card h-100">
                        <div className="card-body">
                          <h6 className="mb-3">{t("categories", "Prestations par catégorie")}</h6>
                          <ul className="list-unstyled mb-0">
                            {metrics?.categories?.length ? (
                              metrics.categories.slice(0, 5).map((cat) => (
                                <li key={cat.category} className="d-flex justify-content-between mb-1">
                                  <span>{cat.category}</span>
                                  <span className="fw-semibold">{formatNumber(cat.total)} {currency}</span>
                                </li>
                              ))
                            ) : (
                              <li className="text-muted">{t("no data", "Aucune donnée")}</li>
                            )}
                          </ul>
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="row g-3">
                    <div className="col-md-6">
                      <label htmlFor="field-cashsessionmodal-18728" className="form-label">{t("counted cash amount", "Montant cash réellement compté")}</label>
                      <input id="field-cashsessionmodal-18728"
                        type="number"
                        className="form-control"
                        value={declaredCash}
                        onChange={(e) => setDeclaredCash(e.target.value)}
                        min="0"
                        step="0.01"
                      />
                    </div>
                    <div className="col-md-6">
                      <label className="form-label">{t("difference", "Écart de caisse")}</label>
                      <div className={`form-control fw-semibold ${difference < 0 ? "text-danger" : "text-success"}`}>
                        {formatNumber(difference)} {currency}
                      </div>
                    </div>
                    <div className="col-12">
                      <label htmlFor="field-cashsessionmodal-19604" className="form-label">{t("note", "Note / justification")}</label>
                      <textarea id="field-cashsessionmodal-19604"
                        className="form-control"
                        rows="2"
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder={t("optional note", "Optionnel")}
                      />
                    </div>
                  </div>
                </>
              ) : (
                <div className="p-2">
                  <div className="alert alert-warning">
                    {t("no open cash session", "Aucune session de caisse ouverte pour cette journée")}
                  </div>
                  <div className="mb-3">
                    <label htmlFor="field-cashsessionmodal-20374" className="form-label">{t("initial float", "Fond de caisse initial")}</label>
                    <input id="field-cashsessionmodal-20374"
                      type="number"
                      className="form-control"
                      value={initialCash}
                      onChange={(e) => setInitialCash(e.target.value)}
                      min="0"
                      step="0.01"
                      placeholder="0"
                    />
                  </div>
                  <div className="mb-3">
                    <label htmlFor="field-cashsessionmodal-20882" className="form-label">{t("note", "Note / justification")}</label>
                    <textarea id="field-cashsessionmodal-20882"
                      className="form-control"
                      rows="2"
                      value={note}
                      onChange={(e) => setNote(e.target.value)}
                      placeholder={t("optional note", "Optionnel")}
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer d-flex justify-content-end flex-wrap gap-2">
              <button type="button" className="btn btn-secondary" onClick={closeAndReset} disabled={loading}>
                {t("close", "Fermer")}
              </button>
              {activeSession ? (
                <button
                  type="button"
                  className="btn btn-success"
                  onClick={handleCloseSession}
                  disabled={loading}
                >
                  {t("validate closure", "Valider la clôture")}
                </button>
              ) : (
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={handleOpenSession}
                  disabled={loading}
                >
                  {t("open cash session", "Ouvrir la caisse")}
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
      <div className="modal-backdrop fade show" onClick={closeAndReset}></div>
      <Display handleClose={() => setDisplay({ ...display, open: false })} {...display} />
    </>
  );
};

CashSessionModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func,
};

export default CashSessionModal;
